import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@ekhodealer/ekho-common/auth-client';
import { ActionMenu, ActionMenuItem, ActionMenuTrigger, Button } from '@ekhodealer/ekho-common/rev';

import Logout24 from '@common/components/icons/Logout24';
import Settings24 from '@common/components/icons/Settings24';
import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';
import { paths } from '@config/paths/index';

import './UserMenu.scss';

/**
 * Extract initials from email or display name
 * Falls back to "?" if neither is available
 */
function getInitials(email?: string | null, displayName?: string | null): string {
	if (displayName) {
		const parts = displayName.trim().split(/\s+/);
		if (parts.length >= 2) {
			return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
		}
		return displayName.substring(0, 2).toUpperCase();
	}

	if (email) {
		// Extract from email: john.doe@example.com -> JD
		const localPart = email.split('@')[0];
		const parts = localPart.split(/[._-]/);
		if (parts.length >= 2) {
			return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
		}
		return localPart.substring(0, 2).toUpperCase();
	}

	return '?';
}

export const UserMenu: React.FC = () => {
	const navigate = useNavigate();
	const { user, signOut, isSigningOut } = useAuth();
	const { data: currentServicesProvider } = useCurrentServicesProvider();
	const servicesProviderId = currentServicesProvider?.id || 'default';

	const initials = useMemo(() => {
		return getInitials(user?.email, user?.displayName);
	}, [user?.email, user?.displayName]);

	const handleSettings = () => {
		navigate(paths.settings.getHref(servicesProviderId));
	};

	const handleLogout = async () => {
		try {
			await signOut();
			navigate(paths.login.getHref(), { replace: true });
		} catch (error) {
			console.error('Error signing out:', error);
		}
	};

	return (
		<ActionMenuTrigger>
			<Button
				ariaLabel='User menu'
				variant='ghost'
				size='icon'
				className='user-menu-trigger'
				isDisabled={isSigningOut}>
				<span className='user-avatar'>{initials}</span>
			</Button>
			<ActionMenu>
				<ActionMenuItem
					label='Settings'
					leadingAccessory={<Settings24 />}
					onAction={handleSettings}
				/>
				<ActionMenuItem
					label='Log out'
					leadingAccessory={<Logout24 />}
					onAction={handleLogout}
				/>
			</ActionMenu>
		</ActionMenuTrigger>
	);
};
